import React from 'react';
import { 
    TextField, 
    Button, 
    Checkbox, 
    Slider, 
    FormControlLabel, 
    Grid, 
    Box, 
    CircularProgress, 
    Typography,
    Card,
    CardContent,
    useTheme,
    useMediaQuery,
    IconButton,
    Collapse
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import VideocamIcon from '@mui/icons-material/Videocam';
import SettingsIcon from '@mui/icons-material/Settings';
import TimerIcon from '@mui/icons-material/Timer';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PreviewIcon from '@mui/icons-material/Preview';
import TuneIcon from '@mui/icons-material/Tune';
import LockdownAppSettingsDialog from '../../../../components/Settings/LockdownAppSettingsDialog';
import MovementZones from '../../../../components/Settings/MovementZones';

// Reusable Setting Item Component with help tooltip
const SettingItem = ({ label, helpText, children, fullWidth = false }) => {
    const [showHelp, setShowHelp] = React.useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    return (
        <Box sx={{ 
            width: '100%',
            mb: { xs: 1, sm: 1.5 }
        }}>
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1
            }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {children}
                </Box>
                {helpText && (
                    <IconButton 
                        size="small" 
                        onClick={() => setShowHelp(!showHelp)}
                        sx={{ 
                            mt: 0.5,
                            color: 'action.active',
                            '&:hover': { color: 'primary.main' }
                        }}
                    >
                        <HelpOutlineIcon fontSize="small" />
                    </IconButton>
                )}
            </Box>
            {helpText && (
                <Collapse in={showHelp}>
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            display: 'block',
                            color: 'text.secondary',
                            bgcolor: 'action.hover',
                            borderRadius: 1,
                            p: 1,
                            mt: 0.5,
                            lineHeight: 1.4
                        }}
                    >
                        {helpText}
                    </Typography>
                </Collapse>
            )}
        </Box>
    );
};

// Section Card Component
const SettingsSection = ({ 
    title, 
    icon: Icon, 
    children
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    return (
        <Card 
            elevation={0}
            sx={{ 
                mb: { xs: 2, sm: 3 },
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden'
            }}
        >
            <Box 
                sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1.5,
                    px: { xs: 2, sm: 2.5 },
                    py: { xs: 1.25, sm: 1.5 },
                    bgcolor: 'grey.50',
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                }}
            >
                {Icon && <Icon sx={{ fontSize: { xs: 18, sm: 20 }, color: 'primary.main' }} />}
                <Typography 
                    variant="subtitle2" 
                    sx={{ 
                        fontWeight: 600,
                        color: 'text.primary',
                        fontSize: { xs: '0.875rem', sm: '0.9rem' }
                    }}
                >
                    {title}
                </Typography>
            </Box>
            <CardContent sx={{ 
                px: { xs: 2, sm: 2.5 },
                py: { xs: 2, sm: 2 },
                '&:last-child': { pb: { xs: 2, sm: 2 } }
            }}>
                {children}
            </CardContent>
        </Card>
    );
};

// Responsive Checkbox Component
const ResponsiveCheckbox = ({ name, checked, onChange, label, disabled, helpText }) => {
    return (
        <SettingItem helpText={helpText}>
            <FormControlLabel
                control={
                    <Checkbox 
                        name={name}
                        checked={checked || false}
                        onChange={onChange}
                        disabled={disabled}
                        sx={{
                            '& .MuiSvgIcon-root': {
                                fontSize: { xs: 24, sm: 24 }
                            }
                        }}
                    />
                }
                label={
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            fontSize: { xs: '0.875rem', sm: '0.9rem' },
                            color: disabled ? 'text.disabled' : 'text.primary'
                        }}
                    >
                        {label}
                    </Typography>
                }
                sx={{
                    m: 0,
                    width: '100%',
                    '& .MuiFormControlLabel-label': {
                        flex: 1
                    }
                }}
            />
        </SettingItem>
    );
};

// Responsive TextField Component
const ResponsiveTextField = ({ name, value, onChange, label, disabled, helpText, type = 'text' }) => {
    return (
        <SettingItem helpText={helpText} fullWidth>
            <TextField
                fullWidth
                size="medium"
                label={label}
                name={name}
                value={value || ''}
                onChange={onChange}
                variant="outlined"
                disabled={disabled}
                type={type}
                sx={{
                    '& .MuiInputBase-input': {
                        fontSize: { xs: '1rem', sm: '0.95rem' },
                        py: { xs: 1.5, sm: 1.25 }
                    },
                    '& .MuiInputLabel-root': {
                        fontSize: { xs: '0.95rem', sm: '0.9rem' }
                    }
                }}
            />
        </SettingItem>
    );
};

const KioskSettingsTab = ({ 
    settings, 
    handleInputChange, 
    user,
    globalTenantId,
    showMovementZonesDialog,
    setShowMovementZonesDialog,
    showLockdownAppSettingsDialog,
    setShowLockdownAppSettingsDialog,
    showDebugLogDialog,
    setShowDebugLogDialog,
    debugLog,
    setDebugLog,
    loadingDebugLog,
    setLoadingDebugLog,
    axios
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
            {/* Movement Detection & Snapshots Section */}
            <SettingsSection title="Movement Detection & Snapshots" icon={VideocamIcon}>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                    {/* Primary Toggle */}
                    <Grid item xs={12}>
                        <ResponsiveCheckbox
                            name="SettingEnableMovementDetection"
                            checked={settings.SettingEnableMovementDetection}
                            onChange={handleInputChange}
                            label="Enable movement detection"
                            helpText="Enable the device to detect movement, take a snapshot, send a notification, and optionally record video"
                        />
                    </Grid>

                    {/* Movement Settings Row */}
                    <Grid item xs={12} sm={6}>
                        <ResponsiveTextField
                            name="SettingMovementNotificationWaitSeconds"
                            value={settings.SettingMovementNotificationWaitSeconds}
                            onChange={handleInputChange}
                            label="Movement waiting (seconds)"
                            disabled={!settings.SettingEnableMovementDetection}
                            helpText="The number of seconds to wait until another movement notification is sent"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <SettingItem helpText="Adjust the sensitivity of movement detection. Lower = more movement required, Higher = more sensitive (more snapshots)">
                            <Typography 
                                variant="body2" 
                                gutterBottom 
                                sx={{ 
                                    color: !settings.SettingEnableMovementDetection ? 'text.disabled' : 'text.primary',
                                    mb: 1
                                }}
                            >
                                Movement Sensitivity
                            </Typography>
                            <Box sx={{ px: 1 }}>
                                <Slider
                                    name="SettingMovementSensitivity"
                                    disabled={!settings.SettingEnableMovementDetection}
                                    value={settings.SettingMovementSensitivity || 15000000}
                                    onChange={handleInputChange}
                                    min={5000000}
                                    max={30000000}
                                    sx={{
                                        '& .MuiSlider-thumb': {
                                            width: { xs: 20, sm: 16 },
                                            height: { xs: 20, sm: 16 }
                                        }
                                    }}
                                />
                            </Box>
                        </SettingItem>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Button 
                            variant="outlined" 
                            onClick={() => setShowMovementZonesDialog(true)}
                            disabled={!settings.SettingEnableMovementDetection}
                            fullWidth={isMobile}
                            sx={{ 
                                py: { xs: 1.25, sm: 1 },
                                fontSize: { xs: '0.9rem', sm: '0.875rem' }
                            }}
                        >
                            Configure Movement Zones
                        </Button>
                    </Grid>

                    {/* Video & Snapshot Options */}
                    <Grid item xs={12} sm={6}>
                        <ResponsiveCheckbox
                            name="SettingEnableMovementVideo"
                            checked={settings.SettingEnableMovementVideo}
                            onChange={handleInputChange}
                            label="Enable movement video"
                            disabled={!settings.SettingEnableMovementDetection}
                            helpText="Allow movement videos to be captured when movement is detected"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <ResponsiveCheckbox
                            name="SettingEnableSnapshots"
                            checked={settings.SettingEnableSnapshots}
                            onChange={handleInputChange}
                            label="Enable scheduled snapshots"
                            helpText="Allow snapshots to be taken on a regular schedule"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <ResponsiveTextField
                            name="SettingSnapshotFrequencySeconds"
                            value={settings.SettingSnapshotFrequencySeconds}
                            onChange={handleInputChange}
                            label="Seconds between snapshots"
                            disabled={!settings.SettingEnableSnapshots}
                            helpText="Enter the frequency in seconds that snapshots will be taken"
                        />
                    </Grid>
                </Grid>
            </SettingsSection>

            {/* Kiosk Behaviour Section */}
            <SettingsSection title="Kiosk Behaviour" icon={SettingsIcon}>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                    <Grid item xs={12} md={6}>
                        <ResponsiveCheckbox
                            name="SettingAllowKioskResponderChoice"
                            checked={settings.SettingAllowKioskResponderChoice}
                            onChange={handleInputChange}
                            label="Allow user to select specific responder (alias)"
                            helpText="Visitors/users can choose a specific responder. Set up aliases from the Aliases menu"
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <ResponsiveCheckbox
                            name="SettingAllowKioskAllResponderChoice"
                            checked={settings.SettingAllowKioskAllResponderChoice}
                            onChange={handleInputChange}
                            label="Allow user to notify all responders"
                            disabled={!settings.SettingAllowKioskResponderChoice}
                            helpText="Only relevant if allowing specific responder choice"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <ResponsiveCheckbox
                            name="SettingSendAllGateNotificationsToKioskAdmins"
                            checked={settings.SettingSendAllGateNotificationsToKioskAdmins}
                            onChange={handleInputChange}
                            label="Send all gate open notifications to kiosk admins"
                            helpText="Ensures all gate notifications go to admins when a number plate or PIN is not assigned to a specific alias"
                        />
                    </Grid>
                </Grid>
            </SettingsSection>

            {/* Call Timeout & Video Messages Section */}
            <SettingsSection title="Call Timeout & Video Messages" icon={TimerIcon}>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                    <Grid item xs={12} sm={6}>
                        <ResponsiveCheckbox
                            name="SettingAllowVoicemail"
                            checked={settings.SettingAllowVoicemail}
                            onChange={handleInputChange}
                            label="Allow visitor to leave a video message"
                            helpText="After a period with no response, visitors can leave a video message"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <ResponsiveTextField
                            name="SettingVisitorTimeoutSeconds"
                            value={settings.SettingVisitorTimeoutSeconds}
                            onChange={handleInputChange}
                            label="Visitor timeout (seconds)"
                            disabled={!settings.SettingAllowVoicemail}
                            helpText="Seconds to wait before visitor timeout (back to main menu)"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <ResponsiveTextField
                            name="SettingVideoMessageWaitSeconds"
                            value={settings.SettingVideoMessageWaitSeconds}
                            onChange={handleInputChange}
                            label="Video message wait (seconds)"
                            disabled={!settings.SettingAllowVoicemail}
                            helpText="Seconds to wait before asking the user to leave a message"
                        />
                    </Grid>
                </Grid>
            </SettingsSection>

            {/* External Camera Settings Section */}
            <SettingsSection title="External Camera Settings" icon={CameraAltIcon}>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                    <Grid item xs={12}>
                        <ResponsiveCheckbox
                            name="SettingUseExternalCamera"
                            checked={settings.SettingUseExternalCamera}
                            onChange={handleInputChange}
                            label="Use external IP camera"
                            helpText="Enable to use an external camera alongside the device's built-in camera"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <ResponsiveTextField
                            name="SettingExternalCameraURL"
                            value={settings.SettingExternalCameraURL}
                            onChange={handleInputChange}
                            label="External camera URL"
                            disabled={!settings.SettingUseExternalCamera}
                            helpText="HLS stream URL. Leave blank to use device IP with Entrinsic RTSP2HTTP app"
                        />
                    </Grid>
                </Grid>
            </SettingsSection>

            {/* Camera Preview Settings Section */}
            <SettingsSection title="Camera Preview Settings" icon={PreviewIcon}>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                    {/* Kiosk Camera Previews */}
                    <Grid item xs={12}>
                        <Typography 
                            variant="subtitle2" 
                            sx={{ 
                                fontWeight: 600, 
                                color: 'text.secondary',
                                mb: 1,
                                borderBottom: 1,
                                borderColor: 'divider',
                                pb: 0.5
                            }}
                        >
                            Kiosk Camera
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <ResponsiveCheckbox
                            name="SettingShowKioskCameraPreviewAlways"
                            checked={settings.SettingShowKioskCameraPreviewAlways}
                            onChange={handleInputChange}
                            label="Always show preview"
                            helpText="Always display the kiosk camera preview"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <ResponsiveCheckbox
                            name="SettingShowKioskCameraPreviewDuringCall"
                            checked={settings.SettingShowKioskCameraPreviewDuringCall}
                            onChange={handleInputChange}
                            label="Show during calls"
                            disabled={settings.SettingShowKioskCameraPreviewAlways}
                            helpText="Show camera preview during video calls"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <ResponsiveCheckbox
                            name="SettingShowKioskCameraPreviewOnMovement"
                            checked={settings.SettingShowKioskCameraPreviewOnMovement}
                            onChange={handleInputChange}
                            label="Show on movement"
                            disabled={settings.SettingShowKioskCameraPreviewAlways}
                            helpText="Show camera preview when movement is detected"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <ResponsiveTextField
                            name="SettingShowKioskCameraPreviewOnMovementTime"
                            value={settings.SettingShowKioskCameraPreviewOnMovementTime}
                            onChange={handleInputChange}
                            label="Movement preview duration (seconds)"
                            type="number"
                            disabled={!settings.SettingShowKioskCameraPreviewOnMovement || settings.SettingShowKioskCameraPreviewAlways}
                            helpText="How long to show the preview after movement is detected"
                        />
                    </Grid>

                    {/* External Camera Previews */}
                    <Grid item xs={12}>
                        <Typography 
                            variant="subtitle2" 
                            sx={{ 
                                fontWeight: 600, 
                                color: 'text.secondary',
                                mt: 1,
                                mb: 1,
                                borderBottom: 1,
                                borderColor: 'divider',
                                pb: 0.5
                            }}
                        >
                            External Camera
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <ResponsiveCheckbox
                            name="SettingShowExternalCameraPreviewAlways"
                            checked={settings.SettingShowExternalCameraPreviewAlways}
                            onChange={handleInputChange}
                            label="Always show preview"
                            disabled={!settings.SettingUseExternalCamera}
                            helpText="Always display the external camera preview"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <ResponsiveCheckbox
                            name="SettingShowExternalCameraPreviewDuringCall"
                            checked={settings.SettingShowExternalCameraPreviewDuringCall}
                            onChange={handleInputChange}
                            label="Show during calls"
                            disabled={!settings.SettingUseExternalCamera || settings.SettingShowExternalCameraPreviewAlways}
                            helpText="Show external camera preview during video calls"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <ResponsiveCheckbox
                            name="SettingShowExternalCameraPreviewOnMovement"
                            checked={settings.SettingShowExternalCameraPreviewOnMovement}
                            onChange={handleInputChange}
                            label="Show on movement"
                            disabled={!settings.SettingUseExternalCamera || settings.SettingShowExternalCameraPreviewAlways}
                            helpText="Show external camera preview when movement is detected"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <ResponsiveTextField
                            name="SettingShowExternalCameraPreviewOnMovementTime"
                            value={settings.SettingShowExternalCameraPreviewOnMovementTime}
                            onChange={handleInputChange}
                            label="Movement preview duration (seconds)"
                            type="number"
                            disabled={!settings.SettingShowExternalCameraPreviewOnMovement || settings.SettingShowExternalCameraPreviewAlways || !settings.SettingUseExternalCamera}
                            helpText="How long to show the external preview after movement is detected"
                        />
                    </Grid>
                </Grid>
            </SettingsSection>

            {/* Miscellaneous Settings Section */}
            <SettingsSection title="Miscellaneous Settings" icon={TuneIcon}>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                    {user.partnerName && (
                        <Grid item xs={12} md={6}>
                            <ResponsiveCheckbox
                                name="SettingEmailOutagesToPartner"
                                checked={settings.SettingEmailOutagesToPartner}
                                onChange={handleInputChange}
                                label="Notify partner about outages"
                                helpText="Notifies your partner about kiosk outages by email"
                            />
                        </Grid>
                    )}

                    <Grid item xs={12} md={6}>
                        <ResponsiveCheckbox
                            name="SettingUseRearCamera"
                            checked={settings.SettingUseRearCamera}
                            onChange={handleInputChange}
                            label="Use rear device camera"
                            helpText="Use the rear camera instead of front-facing. Useful for ANPR with higher image quality"
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <ResponsiveCheckbox
                            name="NotifyOutages"
                            checked={settings.NotifyOutages}
                            onChange={handleInputChange}
                            label="Notify app users about outages"
                            helpText="Notifies Entrinsic Connect app users about kiosk outages"
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <ResponsiveCheckbox
                            name="SettingHighResEnabled"
                            checked={settings.SettingHighResEnabled}
                            onChange={handleInputChange}
                            label="Enable high resolution"
                            helpText="Enable high resolution photo snapshots and video"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <ResponsiveTextField
                            name="SettingAudioAmplificationFactor"
                            value={settings.SettingAudioAmplificationFactor}
                            onChange={handleInputChange}
                            label="Audio amplification factor"
                            helpText="Audio volume multiplier (1 = normal, 2 = double volume)"
                        />
                    </Grid>

                    {/* Custom Action Settings */}
                    <Grid item xs={12}>
                        <Typography 
                            variant="subtitle2" 
                            sx={{ 
                                fontWeight: 600, 
                                color: 'text.secondary',
                                mt: 1,
                                mb: 1,
                                borderBottom: 1,
                                borderColor: 'divider',
                                pb: 0.5
                            }}
                        >
                            Custom Action Button
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <ResponsiveCheckbox
                            name="SettingShowCustomActionButtonInAppKioskMenu"
                            checked={settings.SettingShowCustomActionButtonInAppKioskMenu}
                            onChange={handleInputChange}
                            label="Show in Connect app kiosk menu"
                            helpText="Shows a custom button in the app's kiosk menu that triggers a 'Custom' webhook"
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <ResponsiveCheckbox
                            name="SettingShowCustomActionButtonInAppCall"
                            checked={settings.SettingShowCustomActionButtonInAppCall}
                            onChange={handleInputChange}
                            label="Show during calls"
                            helpText="Shows a custom button during calls that triggers a 'Custom' webhook"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <ResponsiveTextField
                            name="SettingCustomActionLabel"
                            value={settings.SettingCustomActionLabel}
                            onChange={handleInputChange}
                            label="Custom action label"
                            disabled={!settings.SettingShowCustomActionButtonInAppKioskMenu && !settings.SettingShowCustomActionButtonInAppCall}
                            helpText="The label text for the custom action button"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <ResponsiveCheckbox
                            name="SettingCustomWebHookSelectable"
                            checked={settings.SettingCustomWebHookSelectable}
                            onChange={handleInputChange}
                            label="Enable custom webhook selection"
                            disabled={!settings.SettingShowCustomActionButtonInAppKioskMenu && !settings.SettingShowCustomActionButtonInAppCall}
                            helpText="Allow selecting from multiple 'Custom' type webhooks"
                        />
                    </Grid>
                </Grid>
            </SettingsSection>

            {/* Lockdown App Settings - Conditional */}
            {settings.UsesLockdownApp && (
                <SettingsSection title="Lockdown App Settings" icon={SettingsIcon}>
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: { xs: 'center', sm: 'flex-start' }
                    }}>
                        <Button 
                            variant="contained" 
                            color="primary"
                            onClick={() => setShowLockdownAppSettingsDialog(true)}
                            fullWidth={isMobile}
                            sx={{ 
                                py: { xs: 1.5, sm: 1.25 },
                                px: { xs: 3, sm: 4 },
                                fontSize: { xs: '0.95rem', sm: '0.9rem' },
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 500
                            }}
                        >
                            Advanced Settings
                        </Button>
                    </Box>
                </SettingsSection>
            )}

            {/* Debug Log - Conditional */}
            {settings.DebugMode && (
                <SettingsSection title="Debug Log" icon={SettingsIcon}>
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: { xs: 'center', sm: 'flex-start' }
                    }}>
                        <Button 
                            variant="contained" 
                            color="secondary"
                            onClick={async () => {
                                setLoadingDebugLog(true);
                                try {
                                    const response = await axios.get(`/api/tenants/${globalTenantId}/debug-log`);
                                    setDebugLog(response.data.debugLog || 'No debug log available');
                                    setShowDebugLogDialog(true);
                                } catch (error) {
                                    console.error('Error fetching debug log:', error);
                                    alert('Failed to fetch debug log');
                                } finally {
                                    setLoadingDebugLog(false);
                                }
                            }}
                            disabled={loadingDebugLog}
                            fullWidth={isMobile}
                            sx={{ 
                                py: { xs: 1.5, sm: 1.25 },
                                px: { xs: 3, sm: 4 },
                                fontSize: { xs: '0.95rem', sm: '0.9rem' },
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 500
                            }}
                        >
                            {loadingDebugLog ? <CircularProgress size={24} /> : 'View Debug Log'}
                        </Button>
                    </Box>
                </SettingsSection>
            )}

            {/* Dialogs */}
            <LockdownAppSettingsDialog
                open={showLockdownAppSettingsDialog}
                onClose={() => setShowLockdownAppSettingsDialog(false)}
                settings={settings}
            />

            <MovementZones
                open={showMovementZonesDialog}
                onClose={() => setShowMovementZonesDialog(false)}
            />
        </Box>
    );
};

export default KioskSettingsTab;
